import { issuedCredentialsSchema, publicUserSchema, userAccountSchema, } from '@ghp/domain';
import { z } from 'zod';
import { bearer } from './http.js';
const userListSchema = z.array(userAccountSchema);
/**
 * Le cycle complet d'un compte (M6), réservé à l'admin côté serveur — `@Roles('admin')` est posé
 * sur la classe du contrôleur, pas route par route. Rien ici ne le rejoue : **la garde est
 * serveur**, et un filtre de plus dans le navigateur ne protégerait que l'affichage.
 *
 * Le jeton voyage **en argument**, comme les autres portées admin : le back-office n'a que celui
 * de la session en cours, et le rendre explicite oblige l'appelant à décider quoi faire quand il
 * n'y en a pas — au lieu de partir sans identité et de collectionner des 401.
 *
 * ⚠️ **`remove` anonymise, il n'efface pas la ligne** (#654, M6) : `Visit.user_id` et
 * `Inspection.validated_by` doivent continuer à résoudre. Fermer (`deactivate`) se défait,
 * supprimer non.
 */
export function createUsersEndpoints(http) {
    return {
        /**
         * `201` : compte créé, login dérivé et mot de passe tiré **côté serveur**.
         *
         * ⚠️ La réponse porte le mot de passe **en clair, une seule fois** : seul son hachage est
         * stocké, personne ne le retrouve ensuite. À l'écran de le montrer à l'admin, qui le
         * transmet hors bande — et à personne de le journaliser au passage.
         */
        create(accessToken, input) {
            return http.postJson('/users', input, issuedCredentialsSchema, bearer(accessToken));
        },
        /**
         * `200` : **tous** les comptes, actifs comme fermés — pas les supprimés —, triés par `login` côté serveur.
         *
         * Pas de pagination : c'est une dérogation assumée à D24, que le service documente sur place
         * — aucune de ses trois raisons ne s'applique à des comptes qui se comptent en dizaines. Le
         * jour où l'une le devient, c'est l'enveloppe paginée de D24 qu'il faudra, pas un tri ni un
         * filtre ajouté ici.
         */
        list(accessToken) {
            return http.get('/users', userListSchema, bearer(accessToken));
        },
        /**
         * Corrige un compte : le corps ne porte **que ce qui change**. Ni `login` ni `active` n'y
         * entrent — le premier est stocké une fois pour toutes, la seconde est un acte à part.
         *
         * ⚠️ Un changement de rôle **déconnecte la personne**, une correction de nom non : la durée
         * de vie du refresh avait été choisie d'après l'ancien rôle (M23). L'écran doit le dire.
         */
        update(accessToken, id, patch) {
            return http.patchJson(`/users/${id}`, patch, publicUserSchema, bearer(accessToken));
        },
        /**
         * Tire un nouveau mot de passe et **coupe les sessions en cours** (M6/M23). Même réserve que
         * `create` sur la réponse : le mot de passe ne se lit qu'ici.
         *
         * Un corps vide plutôt qu'aucun corps : le pipe du serveur fait passer les deux pour `{}`,
         * mais un `POST` sans `content-type` traverse mal certains proxys.
         */
        resetPassword(accessToken, id) {
            return http.postJson(`/users/${id}/reset-password`, {}, issuedCredentialsSchema, bearer(accessToken));
        },
        /**
         * Ferme un compte : la connexion est refusée et **toutes les sessions sont révoquées**.
         *
         * ⚠️ Le serveur répond `409` sur le **dernier admin actif** — un refus, pas un avertissement :
         * sans admin, la gestion des comptes se ferme à tout le monde. L'écran a ce message à
         * montrer, il ne peut pas le deviner.
         */
        deactivate(accessToken, id) {
            return http.postJson(`/users/${id}/deactivate`, {}, publicUserSchema, bearer(accessToken));
        },
        /**
         * Rouvre un compte fermé, **mot de passe d'avant compris** — le hachage n'a pas été touché.
         * Idempotent : rouvrir un compte déjà ouvert ne fait rien et ne se refuse pas.
         */
        reactivate(accessToken, id) {
            return http.postJson(`/users/${id}/reactivate`, {}, publicUserSchema, bearer(accessToken));
        },
        /**
         * Supprime un compte en l'anonymisant (#654) : `204`, et le compte n'existe plus pour personne.
         *
         * ⚠️ Le serveur répond `409` sur **son propre compte** et sur le **dernier admin actif**.
         */
        remove(accessToken, id) {
            return http.deleteEmpty(`/users/${id}`, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=users.js.map